/* Progressive enhancements for the Liquid-rendered packaging identity.
   No catalog data, prices, purchasing or device APIs live in this file. */
(() => {
  class NFInstruments extends HTMLElement {
    connectedCallback() {
      this.abort?.abort();
      this.abort = new AbortController();
      const toggle = this.querySelector('.nf-art-toggle');
      const cards = [...this.querySelectorAll('.nf-product-card')];
      if (!toggle || !cards.some((card) => card.querySelector('.nf-drawing-image'))) return;
      toggle.hidden = false;
      toggle.addEventListener('click', () => {
        // The visible label names the view the button switches to, so it is
        // the state: "See the drawings" showing means hardware is on screen.
        const drawing = !toggle.querySelector('[data-drawing-label]').hidden;
        toggle.querySelector('[data-drawing-label]').hidden = drawing;
        toggle.querySelector('[data-hardware-label]').hidden = !drawing;
        for (const card of cards) {
          const illustration = card.querySelector('.nf-drawing-image');
          if (!illustration) continue;
          card.querySelector('.nf-hardware-image').hidden = drawing;
          illustration.hidden = !drawing;
        }
      }, { signal: this.abort.signal });
    }

    disconnectedCallback() { this.abort?.abort(); }
  }

  // Local preview of nf_runtime's device controls. Draft entry, current RF
  // state and startup state are separate, exactly as in the hardware Web UI.
  class NFControlDemo extends HTMLElement {
    connectedCallback() {
      this.abort?.abort();
      this.abort = new AbortController();
      const options = { signal: this.abort.signal };
      this.input = this.querySelector('[data-attenuation]');
      if (!this.input) return;
      this.mode ??= 'attenuator';
      this.routes ??= { spdt: 0, sp4t: 0 };
      this.current ??= 31.5;
      this.startup ??= 10;
      this.querySelectorAll('button, input').forEach((control) => { control.disabled = false; });
      this.querySelector('[data-interactive-caption]').hidden = false;
      this.input.addEventListener('input', () => this.clearError(), options);
      this.input.addEventListener('keydown', (event) => {
        if (event.key === 'Enter') { event.preventDefault(); this.setAttenuation(); }
      }, options);
      this.querySelector('[data-apply-attenuation]').addEventListener('click', () => this.setAttenuation(), options);
      this.querySelector('[data-save-startup]').addEventListener('click', () => this.setAttenuation(true), options);
      this.querySelectorAll('[data-instrument]').forEach((button) => button.addEventListener('click', () => this.selectInstrument(button.dataset.instrument), options));
      this.querySelectorAll('[data-route]').forEach((button) => button.addEventListener('click', () => this.selectRoute(Number(button.dataset.route)), options));
      this.setupDeviceTabs(options);
      this.dialog = this.querySelector('dialog');
      if (typeof this.dialog.showModal === 'function') {
        this.querySelector('[data-enlarge-diagram]').hidden = false;
        this.querySelector('[data-enlarge-diagram]').addEventListener('click', () => this.enlargeDiagram(), options);
        this.querySelector('[data-close-diagram]').addEventListener('click', () => this.dialog.close(), options);
        this.dialog.addEventListener('close', () => {
          if (this.isConnected) this.querySelector('[data-enlarge-diagram]').focus({ preventScroll: true });
        }, options);
      }
      this.selectInstrument(this.mode);
    }

    disconnectedCallback() { this.abort?.abort(); this.dialog?.close(); }

    // System / Network / Firmware card. Like the device, clicking the open tab
    // collapses it. Save, reset and install only report that this is a preview.
    setupDeviceTabs(options) {
      this.deviceTab ??= 'system';
      this.querySelectorAll('[data-device-tab]').forEach((tab) => tab.addEventListener('click', () => {
        this.selectDeviceTab(this.deviceTab === tab.dataset.deviceTab ? '' : tab.dataset.deviceTab);
      }, options));
      this.querySelectorAll('[data-network] input[type="radio"]').forEach((radio) => radio.addEventListener('change', () => this.syncNetwork(), options));
      this.querySelectorAll('[data-preview-action]').forEach((button) => button.addEventListener('click', () => {
        this.querySelector('[data-device-notice]').textContent = this.dataset.previewOnly;
      }, options));
      const url = this.querySelector('[data-firmware-url]');
      url.addEventListener('input', () => this.syncFirmware(), options);
      const dropzone = this.querySelector('[data-dropzone]');
      // Without these the browser would open a dropped file in place of the page.
      dropzone.addEventListener('dragover', (event) => { event.preventDefault(); dropzone.classList.add('is-drag-over'); }, options);
      dropzone.addEventListener('dragleave', () => dropzone.classList.remove('is-drag-over'), options);
      dropzone.addEventListener('drop', (event) => {
        event.preventDefault();
        dropzone.classList.remove('is-drag-over');
        this.querySelector('[data-device-notice]').textContent = this.dataset.previewOnly;
      }, options);
      this.selectDeviceTab(this.deviceTab);
      this.syncNetwork();
      this.syncFirmware();
    }

    selectDeviceTab(name) {
      this.deviceTab = name;
      this.querySelector('[data-device-notice]').textContent = '';
      this.querySelectorAll('[data-device-tab]').forEach((tab) => tab.setAttribute('aria-expanded', String(tab.dataset.deviceTab === name)));
      this.querySelectorAll('[data-device-panel]').forEach((panel) => { panel.hidden = panel.dataset.devicePanel !== name; });
    }

    syncNetwork() {
      const mode = (name) => this.querySelector(`[data-network="${name}"] input[type="radio"]:checked`).value;
      const eth = mode('ethernet');
      this.querySelectorAll('[data-eth-static]').forEach((input) => { input.disabled = eth === 'dhcp_client'; });
      this.querySelector('[data-eth-timeout]').hidden = eth !== 'dhcp_client_fallback';
      const label = this.querySelector('[data-eth-ip-label]');
      label.textContent = eth === 'dhcp_client_fallback' ? label.dataset.fallbackLabel : label.dataset.staticLabel;
      const usbc = mode('usbc');
      this.querySelector('[data-usbc-fields]').hidden = usbc === 'disabled';
      this.querySelector('[data-usbc-dhcp]').hidden = usbc !== 'dhcp_host';
      this.querySelector('[data-usbc-save]').disabled = usbc === 'disabled';
    }

    syncFirmware() {
      this.querySelector('[data-firmware-install]').disabled = !this.querySelector('[data-firmware-url]').value;
    }

    enlargeDiagram() {
      const source = this.mode === 'attenuator'
        ? this.querySelector('[data-atten-controls] svg')
        : this.querySelector(`[data-diagram="${this.mode}-${this.routes[this.mode]}"]`);
      const copy = source.cloneNode(true);
      copy.hidden = false;
      // Scope any SVG IDs/references so opening the preview never duplicates
      // identifiers in the original diagram or another theme-editor section.
      const ids = [copy, ...copy.querySelectorAll('[id]')].filter((el) => el.id).map((el) => el.id);
      for (const el of [copy, ...copy.querySelectorAll('*')]) {
        if (el.id) el.id += '-enlarged';
        el.removeAttribute('data-atten-diagram-value');
        el.removeAttribute('data-diagram');
        for (const attr of [...el.attributes]) {
          let value = attr.value;
          for (const id of ids) value = value === `#${id}` ? `#${id}-enlarged` : value.replace(`url(#${id})`, `url(#${id}-enlarged)`);
          if (value !== attr.value) el.setAttribute(attr.name, value);
        }
      }
      if (copy.tagName === 'IMG') copy.loading = 'eager';
      // A fresh scroll container avoids WebKit restoring the previous pane's
      // offsets after the new diagram finishes its opening layout.
      const previousRegion = this.querySelector('[data-enlarged-diagram]');
      const region = previousRegion.cloneNode(false);
      region.append(copy);
      previousRegion.replaceWith(region);
      this.dialog.showModal();
      this.querySelector('[data-close-diagram]').focus({ preventScroll: true });
      const resetScroll = () => {
        if (!this.dialog.open) return;
        region.scrollTo(0, 0);
        this.dialog.scrollTo(0, 0);
      };
      resetScroll();
      // WebKit may restore the previous dialog offset during opening layout.
      requestAnimationFrame(resetScroll);
    }


    clearError() {
      this.input.removeAttribute('aria-invalid');
      const error = this.querySelector('[data-error]');
      error.hidden = true;
      error.textContent = '';
    }

    setAttenuation(startup = false) {
      const value = this.input.valueAsNumber;
      if (!this.input.validity.valid || !Number.isFinite(value) || value < 0 || value > 95.25 || !Number.isInteger(value * 4)) {
        const error = this.querySelector('[data-error]');
        error.textContent = this.dataset.invalidSetpoint;
        error.hidden = false;
        this.input.setAttribute('aria-invalid', 'true');
        this.input.focus();
        return;
      }
      this.clearError();
      if (startup) this.startup = value;
      else this.current = value;
      this.querySelector('[data-current]').textContent = this.current.toFixed(2);
      this.querySelector('[data-startup]').textContent = this.startup.toFixed(2);
      this.querySelector('[data-atten-diagram-value]').textContent = `${this.current} dB`;
    }

    selectInstrument(mode) {
      if (!['attenuator', 'spdt', 'sp4t'].includes(mode)) return;
      this.mode = mode;
      this.clearError();
      const attenuation = mode === 'attenuator';
      this.querySelectorAll('[data-instrument]').forEach((button) => {
        button.setAttribute('aria-pressed', String(button.dataset.instrument === mode));
        if (button.dataset.instrument !== mode) return;
        for (const field of ['hostname', 'model', 'serial']) this.querySelector(`[data-system="${field}"]`).textContent = button.dataset[field];
      });
      this.querySelector('[data-atten-controls]').hidden = !attenuation;
      this.querySelector('[data-switch-controls]').hidden = attenuation;
      if (!attenuation) {
        const count = mode === 'spdt' ? 2 : 4;
        this.querySelector('.nf-route-buttons').style.setProperty('--nf-route-count', count);
        this.querySelectorAll('[data-route]').forEach((button) => { button.hidden = Number(button.dataset.route) >= count; });
        this.selectRoute(this.routes[mode]);
      }
    }

    selectRoute(route) {
      const count = this.mode === 'spdt' ? 2 : this.mode === 'sp4t' ? 4 : 0;
      if (!Number.isInteger(route) || route < 0 || route >= count) return;
      this.routes[this.mode] = route;
      this.querySelectorAll('[data-route]').forEach((button) => button.setAttribute('aria-pressed', String(Number(button.dataset.route) === route)));
      this.querySelectorAll('[data-diagram]').forEach((diagram) => { diagram.hidden = diagram.dataset.diagram !== `${this.mode}-${route}`; });
      this.querySelector('[data-route-description]').textContent = `${this.dataset.routeDescription} RF${route}`;
    }
  }

  if (!customElements.get('nf-instruments')) customElements.define('nf-instruments', NFInstruments);
  if (!customElements.get('nf-control-demo')) customElements.define('nf-control-demo', NFControlDemo);
})();
